import React, { createContext, useContext, useState, useEffect } from 'react';
import * as api from '../api/productApi';
import { message } from 'antd';

interface ProductContextProps {
    products: api.Product[];
    loading: boolean;
    item: api.Product | undefined;
    fetchProducts: () => Promise<void>;
    createProduct: (data: api.Product) => Promise<void>;
    updateProduct: (id: string, data: api.Product) => Promise<void>;
    deleteProduct: (id: string) => Promise<void>;
    fetchProduct: (id: string) => Promise<void>;
}

const ProductContext = createContext<ProductContextProps | undefined>(undefined);

export const ProductProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [products, setProducts] = useState<api.Product[]>([]);
    const [item, setItem] = useState<api.Product>();
    const [loading, setLoading] = useState(false);

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const res = await api.getProducts();
            setProducts(res.data);
        } catch (err) {
            message.error('Failed to fetch products');
        }
        setLoading(false);
    };
    const fetchProduct = async (id: string) => {
        try {
            const res = await api.getProduct(id);
            setItem(res.data);
        } catch (err) {
            message.error('Failed to fetch products');
        }
    };
    const createProductHandler = async (data: api.Product) => {
        try {
            await api.createProduct(data);
            message.success('Product created');
            fetchProducts();
        } catch (err) {
            message.error('Failed to create product');
        }
    };

    const updateProductHandler = async (id: string, data: api.Product) => {
        try {
            await api.updateProduct(id, data);
            message.success('Product updated');
            fetchProducts();
        } catch (err) {
            message.error('Failed to update product');
        }
    };

    const deleteProductHandler = async (id: string) => {
        try {
            await api.deleteProduct(id);
            message.success('Product deleted');
            fetchProducts();
        } catch (err) {
            message.error('Failed to delete product');
        }
    };

    useEffect(() => { fetchProducts(); }, []);

    return (
        <ProductContext.Provider value={{
            products, loading, item,
            fetchProducts,
            createProduct: createProductHandler,
            updateProduct: updateProductHandler,
            deleteProduct: deleteProductHandler,
            fetchProduct
        }}>
            {children}
        </ProductContext.Provider>
    );
};

export const useProduct = () => {
    const ctx = useContext(ProductContext);
    if (!ctx) throw new Error('useProduct must be used within ProductProvider');
    return ctx;
};
